import * as v from "valibot";
import {
  InferFormOutput,
  createSchemaWithIntl,
  isOrdered,
} from "../utils/validation";
import { NaiveDate } from "@/utils/date";
import { useFieldArray, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { DatePicker } from "@/kit/ui/date-picker";
import { Button } from "@/kit/ui/button";
import { ClockIcon, PlusCircleIcon, Trash01Icon, CalendarIcon } from "@/icons";
import { Input } from "@/kit/ui/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/kit/ui/form";
import { DirtyFields, Nullable } from "@/utils/types";
import { Combobox } from "@/kit/ui/combobox";
import { ConfirmDialog } from "./ConfirmDialog";
import { MouseEvent } from "react";
import { Switch } from "@/kit/ui/switch";

const MAX_STEPS = 10;

const formSchema = createSchemaWithIntl((intl) =>
  v.pipe(
    v.object({
      timezone: v.string(
        intl.formatMessage({
          defaultMessage:
            "Timezone is required. Please select your timezone from the list.",
        }),
      ),

      online: v.optional(v.boolean(), false),

      dateRange: v.pipe(
        v.object(
          {
            from: v.instance(
              NaiveDate,
              intl.formatMessage({
                defaultMessage:
                  "Start date is invalid. Please enter a valid date.",
              }),
            ),
            to: v.instance(
              NaiveDate,
              intl.formatMessage({
                defaultMessage:
                  "End date is invalid. Please enter a valid date.",
              }),
            ),
          },
          intl.formatMessage({
            defaultMessage:
              "Please provide both a start and an end date to define the date range.",
          }),
        ),
        v.check(
          ({ from, to }) => to > from,
          ({ input: { from, to } }) =>
            intl.formatMessage(
              {
                defaultMessage:
                  "The end date ({to}) must be after the start date ({from}). Please correct the order.",
              },
              {
                from: intl.formatDate(from.toLocale()),
                to: intl.formatDate(to.toLocale()),
              },
            ),
        ),
      ),

      steps: v.optional(
        v.pipe(
          v.array(
            v.object({
              date: v.instance(
                NaiveDate,
                intl.formatMessage({
                  defaultMessage:
                    "Each step must have a valid date. Please review the step dates.",
                }),
              ),
              label: v.pipe(
                v.string(),
                v.minLength(
                  1,
                  intl.formatMessage({
                    defaultMessage:
                      "Step label is required. It cannot be empty.",
                  }),
                ),
                v.maxLength(
                  20,
                  intl.formatMessage({
                    defaultMessage:
                      "Step label is too long. Maximum 20 characters allowed.",
                  }),
                ),
              ),
            }),
          ),
          v.maxLength(
            MAX_STEPS,
            intl.formatMessage(
              {
                defaultMessage:
                  "You can add up to {size} steps only. Please remove any extra steps.",
              },
              { size: MAX_STEPS },
            ),
          ),
          v.check(
            (steps) => !steps || isOrdered(steps.map(({ date }) => date)),
            intl.formatMessage({
              defaultMessage:
                "Step dates must be in chronological order (earliest to latest). Please rearrange them.",
            }),
          ),
        ),
      ),
    }),
    v.check(
      ({ dateRange: { from, to }, steps }) => {
        if (!steps) return true;

        return steps.every(({ date }) => {
          return date > from && date < to;
        });
      },
      ({
        input: {
          dateRange: { from, to },
        },
      }) =>
        intl.formatMessage(
          {
            defaultMessage:
              "Each step date must be strictly within the selected date range: {range} (excluding the start and end dates)",
          },
          {
            range: intl.formatDateTimeRange(from.toLocale(), to.toLocale()),
          },
        ),
    ),
  ),
);

type FormData = InferFormOutput<typeof formSchema>;

interface SubmitData {
  data: FormData;
  dirtyFields: DirtyFields<FormData>;
}

interface Props {
  onSubmit: (data: SubmitData) => void;
  onDelete: (event: MouseEvent) => void;
  defaultValues: Nullable<FormData>;
  isDisabled?: boolean;
}

export default function TimelineEditForm({
  onSubmit,
  onDelete,
  defaultValues,
  isDisabled,
}: Props) {
  const intl = useIntl();
  const form = useForm<FormData>({
    mode: "onTouched",
    defaultValues: {
      dateRange: defaultValues.dateRange ?? undefined,
      steps: defaultValues.steps ?? undefined,
      online: defaultValues.online ?? false,
      timezone:
        defaultValues.timezone ??
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    resolver: valibotResolver(formSchema(intl)),
  });
  const {
    control,
    formState: { dirtyFields },
    handleSubmit,
  } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "steps",
  });

  const doSubmit = handleSubmit((data) => {
    onSubmit({
      data,
      dirtyFields,
    });
  });

  const timeZones = Intl.supportedValuesOf("timeZone");

  return (
    <Form form={form} onSubmit={doSubmit} aria-labelledby="timeline-form-title">
      <div className="flex flex-row justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-grey-900">
            <FormattedMessage defaultMessage="Competition timeline configurator" />
          </h1>
          <p className="text-sm font-thin text-grey-600">
            <FormattedMessage defaultMessage="Fine-tune key dates and milestones to manage your competition schedule." />
          </p>
        </div>
        <div className="flex flex-row items-baseline gap-4">
          <ConfirmDialog
            onConfirm={onDelete}
            kind="danger"
            title={
              <FormattedMessage defaultMessage="Are you sure you want delete this timeline?" />
            }
            buttonMessage={<FormattedMessage defaultMessage="Delete" />}
            message={
              <FormattedMessage defaultMessage="By clicking on the button below, this will permanently delete the timeline." />
            }
          />
          <Button
            type="submit"
            onClick={doSubmit}
            disabled={isDisabled}
            className="mt-4"
            aria-label={intl.formatMessage({
              defaultMessage: "Save timeline",
            })}
          >
            <FormattedMessage defaultMessage="Save Timeline" />
          </Button>
        </div>
      </div>
      <div className="flex flex-col bg-white rounded-lg border border-grey-200 p-8">
        <p className="text-lg font-semibold mb-4">
          <FormattedMessage defaultMessage="Competition duration & time zone" />
        </p>
        <FormMessage />
        <div className="flex flex-col gap-6">
          <FormField
            control={control}
            name="online"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center gap-4" checkbox>
                <FormControl>
                  <Switch {...field} />
                </FormControl>
                <div>
                  <FormLabel className="text-md">
                    <FormattedMessage defaultMessage="Is Online" />
                  </FormLabel>
                  <FormDescription>
                    <FormattedMessage defaultMessage="Is the competition taking place virtually or in person?" />
                  </FormDescription>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
          <div className="flex flex-row items-baseline w-full gap-4">
            <FormField
              control={control}
              name="dateRange"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    <FormattedMessage defaultMessage="Competition duration" />
                  </FormLabel>
                  <FormControl>
                    <DatePicker
                      mode="range"
                      startDecorator={<CalendarIcon />}
                      placeholder={intl.formatMessage({
                        defaultMessage: "Pick a date range",
                      })}
                      disabled={field.disabled || isDisabled}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="timezone"
              render={({ field }) => {
                return (
                  <FormItem className="w-full">
                    <FormLabel>
                      <FormattedMessage defaultMessage="Time zone" />
                    </FormLabel>
                    <FormControl>
                      <Combobox
                        items={timeZones.map((zone) => ({
                          value: zone,
                          label: zone,
                        }))}
                        emptyMessage={intl.formatMessage({
                          defaultMessage: "No timezone found",
                        })}
                        placeholder={intl.formatMessage({
                          defaultMessage: "Choose the relevant time zone",
                        })}
                        startDecorator={<ClockIcon />}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <div>
              <p className="text-lg font-semibold">
                <FormattedMessage defaultMessage="Competition milestones" />
              </p>
              <p className="text-[0.8rem] text-muted-foreground">
                <FormattedMessage defaultMessage="Define key milestones within the competition timeline." />
              </p>
            </div>
            <FormField
              control={control}
              name="steps"
              render={() => (
                <FormItem>
                  <div className="flex flex-col gap-6 my-4">
                    {fields.map((_, index) => (
                      <div
                        key={`steps.${index}`}
                        className="flex-col inline-flex justify-between items-end gap-4 w-full bg-grey-50 p-4 rounded-lg"
                      >
                        <div className="flex flex-row w-full justify-between items-center">
                          <p>
                            <FormattedMessage
                              defaultMessage="#{index} milestone"
                              values={{ index: index + 1 }}
                            />
                          </p>
                          <Button
                            variant="secondary"
                            type="button"
                            onClick={() => remove(index)}
                            disabled={isDisabled}
                            aria-label={intl.formatMessage({
                              defaultMessage: "Remove milestone",
                            })}
                          >
                            <Trash01Icon />
                            <FormattedMessage defaultMessage="Remove milestone" />
                          </Button>
                        </div>
                        <div className="flex flex-row items-baseline justify-between w-full gap-4">
                          <FormField
                            control={control}
                            name={`steps.${index}.label`}
                            render={({ field }) => (
                              <FormItem className="w-full">
                                <FormLabel>
                                  <FormattedMessage defaultMessage="Milestone name" />
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={intl.formatMessage({
                                      defaultMessage: "E.g. Competition start",
                                    })}
                                    disabled={isDisabled}
                                    aria-label={intl.formatMessage({
                                      defaultMessage: "Milestone description",
                                    })}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={control}
                            name={`steps.${index}.date`}
                            render={({ field }) => (
                              <FormItem className="w-full">
                                <FormLabel>
                                  <FormattedMessage
                                    defaultMessage="Date range"
                                    values={{ index: index + 1 }}
                                  />
                                </FormLabel>
                                <FormControl>
                                  <DatePicker
                                    mode="single"
                                    startDecorator={<CalendarIcon />}
                                    disabled={field.disabled || isDisabled}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => append({ label: "", date: new NaiveDate() })}
                    disabled={fields.length >= MAX_STEPS || isDisabled}
                    className="w-full flex items-center justify-center gap-2"
                    aria-label={intl.formatMessage({
                      defaultMessage: "Add a milestone",
                    })}
                  >
                    <PlusCircleIcon />
                    <FormattedMessage defaultMessage="Add a milestone" />
                  </Button>
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    </Form>
  );
}
